import React, { useState } from 'react';
import { createOrder } from '../../services/orderService';

const QuickOrderForm = ({
    product,
    collection,
    selectedColor,
    selectedSize,
    selectedFeatures,
    quantity,
    isButtonDisabled,
    isOutOfStock,
    onOrderSuccess
}) => {
    const [formData, setFormData] = useState({
        username: '',
        phoneNumber: '',
        email: '',
        street: '',
        gouvernorate: '',
        city: '',
        postalCode: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
    const [submitMessage, setSubmitMessage] = useState('');

    const governates = [
        'Ariana', 'Béja', 'Ben Arous', 'Bizerte', 'Gabès', 'Gafsa', 'Jendouba',
        'Kairouan', 'Kasserine', 'Kébili', 'Le Kef', 'Mahdia', 'La Manouba',
        'Médenine', 'Monastir', 'Nabeul', 'Sfax', 'Sidi Bouzid', 'Siliana',
        'Sousse', 'Tataouine', 'Tozeur', 'Tunis', 'Zaghouan'
    ];

    const validateForm = () => {
        const newErrors = {};

        // Username validation
        if (!formData.username.trim()) {
            newErrors.username = 'Le nom est requis';
        } else if (formData.username.trim().length < 2) {
            newErrors.username = 'Le nom doit comporter au moins 2 caractères';
        }

        // Phone validation
        const phoneRegex = /^[+]?[\d\s\-()]+$/;
        if (!formData.phoneNumber.trim()) {
            newErrors.phoneNumber = 'Le numéro de téléphone est requis';
        } else if (!phoneRegex.test(formData.phoneNumber.trim())) {
            newErrors.phoneNumber = 'Format de téléphone invalide';
        }

        // Email validation (optional)
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Format d\'email invalide';
        }

        // Address validation
        if (!formData.street.trim()) {
            newErrors.street = 'L\'adresse est requise';
        }
        if (!formData.gouvernorate) {
            newErrors.gouvernorate = 'Le gouvernorat est requis';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field when user types
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitStatus(null);
        setSubmitMessage('');

        if (isButtonDisabled) {
            setSubmitMessage('Veuillez sélectionner des options valides avant de commander.');
            setSubmitStatus('error');
            return;
        }

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            // Construct product object for order
            const orderProduct = {
                productId: product.productId, // Required by backend
                productName: product.name,
                unitPrice: parseFloat(product.price),
                quantity: parseInt(quantity, 10),
                totalPrice: parseFloat(product.price) * parseInt(quantity, 10),
                options: {
                    collection: collection?.name,
                    color: selectedColor?.label || selectedColor?.id,
                    size: selectedSize,
                    features: selectedFeatures && selectedFeatures.length > 0 ?
                        selectedFeatures.reduce((acc, curr) => ({ ...acc, [curr.name]: curr.value }), {}) :
                        undefined
                },
                images: [selectedColor?.imagePath || product.img] // Changed to match backend schema (array)
            };

            const orderPayload = {
                username: formData.username,
                email: formData.email,
                phoneNumber: formData.phoneNumber,
                address: {
                    street: formData.street,
                    gouvernorate: formData.gouvernorate,
                    city: formData.city,
                    postalCode: formData.postalCode
                },
                products: [orderProduct],
                totalAmount: orderProduct.totalPrice
            };

            const result = await createOrder(orderPayload);

            if (result.success) {
                setSubmitStatus('success');
                setSubmitMessage(`Commande effectuée avec succès ! Numéro de commande : ${result.data.orderNumber}`);
                // Reset form
                setFormData({
                    username: '',
                    phoneNumber: '',
                    email: '',
                    street: '',
                    gouvernorate: '',
                    city: '',
                    postalCode: ''
                });
                if (onOrderSuccess) {
                    onOrderSuccess(result.data);
                }
            } else {
                setSubmitStatus('error');
                // Filter out "failed to reduce stock" errors
                let errorMessage = result.error || 'Échec de la commande. Veuillez réessayer.';
                if (errorMessage.toLowerCase().includes('failed to reduce stock') ||
                    errorMessage.toLowerCase().includes('reduce stock')) {
                    errorMessage = 'Impossible de traiter la commande. La quantité demandée peut dépasser le stock disponible. Veuillez vérifier votre sélection et réessayer.';
                }
                setSubmitMessage(errorMessage);
                // Check if error is related to total amount mismatch
                if (result.code === 'TOTAL_AMOUNT_MISMATCH') {
                    // Handle specific error logic if needed, e.g. refresh price
                }
            }
        } catch (err) {
            setSubmitStatus('error');
            setSubmitMessage('Une erreur inattendue s\'est produite.');
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="quick-order-form-container mt-30 mb-30 p-4 border rounded" style={{ backgroundColor: '#f9f9f9' }}>

            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                <h4 className="title mb-0 mb-md-4">Commande Rapide</h4>
                {(isButtonDisabled || isOutOfStock) && (
                    <p className="text-danger mt-0 mt-md-2" style={{ fontSize: '13px' }}>
                        {isOutOfStock ? 'Cette combinaison est en rupture de stock.' : 'Veuillez sélectionner toutes les options requises pour commander.'}
                    </p>
                )}
            </div>

            {submitStatus === 'success' && (
                <div className="alert alert-success" role="alert">
                    {submitMessage}
                </div>
            )}

            {submitStatus === 'error' && (
                <div className="alert alert-danger" role="alert">
                    {submitMessage}
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="row">
                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label htmlFor="username">Nom <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                                id="username"
                                name="username"
                                value={formData.username}
                                onChange={handleChange}
                                placeholder="Votre Nom"
                            />
                            {errors.username && <div className="invalid-feedback">{errors.username}</div>}
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label htmlFor="phoneNumber">Téléphone <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className={`form-control ${errors.phoneNumber ? 'is-invalid' : ''}`}
                                id="phoneNumber"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                                placeholder="Votre Numéro de téléphone"
                            />
                            {errors.phoneNumber && <div className="invalid-feedback">{errors.phoneNumber}</div>}
                        </div>
                    </div>

                    <div className="col-md-12">
                        <div className="form-group mb-3">
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Votre Email (Optionnel)"
                            />
                            {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                        </div>
                    </div>

                    <div className="col-md-12">
                        <div className="form-group mb-3">
                            <label htmlFor="street">Adresse <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className={`form-control ${errors.street ? 'is-invalid' : ''}`}
                                id="street"
                                name="street"
                                value={formData.street}
                                onChange={handleChange}
                                placeholder="Adresse"
                            />
                            {errors.street && <div className="invalid-feedback">{errors.street}</div>}
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label htmlFor="gouvernorate">Gouvernorat <span className="text-danger">*</span></label>
                            <select
                                className={`form-control ${errors.gouvernorate ? 'is-invalid' : ''}`}
                                id="gouvernorate"
                                name="gouvernorate"
                                value={formData.gouvernorate}
                                onChange={handleChange}
                            >
                                <option value="">Sélectionner le Gouvernorat</option>
                                {governates.map(gov => (
                                    <option key={gov} value={gov}>{gov}</option>
                                ))}
                            </select>
                            {errors.gouvernorate && <div className="invalid-feedback">{errors.gouvernorate}</div>}
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label htmlFor="city">Ville</label>
                            <input
                                type="text"
                                className="form-control"
                                id="city"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                placeholder="Ville"
                            />
                        </div>
                    </div>

                    <div className="col-md-4">
                        <div className="form-group mb-3">
                            <label htmlFor="postalCode">Code Postal</label>
                            <input
                                type="text"
                                className="form-control"
                                id="postalCode"
                                name="postalCode"
                                value={formData.postalCode}
                                onChange={handleChange}
                                placeholder="Code Postal"
                            />
                        </div>
                    </div>
                </div>

                <div className="row mt-3">
                    <div className="col-12 d-flex flex-column flex-md-row align-items-center justify-content-between">
                        <div className="cart-plus-minus mb-0 mb-md-0" style={{ display: 'inline-block', marginRight: '0px', whiteSpace: 'nowrap' }}>
                            <span style={{ marginRight: '10px', fontWeight: 'bold' }}>Total:</span>
                            <span style={{ fontSize: '18px', color: '#f3b806', fontWeight: 'bold' }}>
                                {((parseFloat(product.price) || 0) * parseInt(quantity || 1, 10)).toFixed(2)} TND
                            </span>
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting || isButtonDisabled || isOutOfStock}
                            style={{
                                backgroundColor: isButtonDisabled || isOutOfStock ? '#ccc' : '#f3b806',
                                borderColor: isButtonDisabled || isOutOfStock ? '#ccc' : '#f3b806',
                                cursor: isButtonDisabled || isOutOfStock ? 'not-allowed' : 'pointer',
                                padding: '10px 30px',
                                color: '#fff',
                                borderRadius: '5px',
                                marginTop: '0px'
                            }}
                        >
                            {isSubmitting ? 'Traitement...' : (isOutOfStock ? 'Rupture de stock' : 'Commander')}
                        </button>

                    </div>

                </div>
            </form>
        </div>
    );
};

export default QuickOrderForm;
